/**
 * MemoryRecapPill — organiser-only "Send the recap ✦" surface that
 * sits next to the "Create a Memory Chapter" pill on EventPage.
 *
 * Design intent matches the keepsake — restrained, italic-serif,
 * dark glass with a gold hairline. NEVER reads as a marketing CTA.
 *
 * Lifecycle: pulls /memory-recap/status to decide between "Send the
 * recap" (pre-send) and "Recap delivered · n guests" (post-send).
 * Opening the modal previews the rendered HTML inside an iframe so
 * the host can eyeball it before pressing send. A "force resend"
 * checkbox is hidden by default and reveals when a recap has
 * already been delivered, so re-curators can refresh.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { brandToast } from '../lib/brandToast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeader = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const pillBase = {
  display: 'inline-flex', alignItems: 'center', gap: 10,
  height: 42, padding: '0 18px', borderRadius: 999,
  background: 'rgba(20,12,6,0.6)',
  border: '1px solid rgba(212,175,94,0.45)',
  color: '#FFE7A8',
  fontWeight: 700, fontSize: 12, letterSpacing: '0.02em',
  cursor: 'pointer',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 0 18px rgba(212,175,94,0.18)',
  fontFamily: 'Georgia, "Times New Roman", serif',
  fontStyle: 'italic',
};

const sentPill = {
  ...pillBase,
  background: 'rgba(20,12,6,0.55)',
  border: '1px solid rgba(212,175,94,0.30)',
  color: '#E2C98E',
  cursor: 'default',
  fontWeight: 500,
  letterSpacing: '0.06em',
};

const overlayStyle = {
  position: 'fixed', inset: 0,
  background: 'rgba(8, 4, 0, 0.78)',
  backdropFilter: 'blur(8px)',
  zIndex: 90,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '24px',
};

const modalStyle = {
  width: 'min(820px, 100%)',
  maxHeight: '88vh',
  display: 'flex', flexDirection: 'column',
  background: '#1a1108',
  border: '1px solid rgba(212,175,94,0.30)',
  borderRadius: 16,
  overflow: 'hidden',
  boxShadow: '0 30px 80px rgba(0,0,0,0.6)',
};

export default function MemoryRecapPill({ event }) {
  const eventId = event?.event_id;
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [sending, setSending] = useState(false);
  const [force, setForce] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Pull recap status. We poll lightly (no SSE) — the host opens this
  // surface intentionally so a stale 30s read is acceptable.
  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/events/${eventId}/memory-recap/status`, { headers: authHeader() });
        if (!cancelled) setStatus(r.data);
      } catch {
        if (!cancelled) setStatus({ recipients: 0, delivered: 0, last_sent_at: null });
      }
    };
    load();
    return () => { cancelled = true; };
  }, [eventId]);

  // Only surface this pill when memory snap is part of the event package
  // (matches the chapter pill above it). Anchored to the same `features`
  // check so we don't promote recap as a feature where it's irrelevant.
  const featureOn = Array.isArray(event?.features) && event.features.includes('memory_snap');
  if (!featureOn) return null;

  const recipients = status?.recipients ?? 0;
  const delivered = status?.delivered ?? 0;
  const lastSent = status?.last_sent_at || null;

  const openModal = async () => {
    setOpen(true);
    setLoadingPreview(true);
    try {
      const r = await axios.get(`${API}/events/${eventId}/memory-recap/preview`, {
        headers: authHeader(),
        responseType: 'text',
      });
      setPreviewHtml(r.data || '');
    } catch {
      setPreviewHtml('<div style="padding:40px;font-family:Georgia;font-style:italic;color:#A8854C;text-align:center">A preview could not be composed yet ✦</div>');
    } finally {
      setLoadingPreview(false);
    }
  };

  const closeModal = () => {
    setOpen(false);
    setForce(false);
  };

  const handleSend = async () => {
    if (sending) return;
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/events/${eventId}/memory-recap/send`,
        { force },
        { headers: authHeader() },
      );
      const sent = r.data?.sent ?? 0;
      const skipped = r.data?.skipped ?? 0;
      if (sent > 0) {
        brandToast.gentle(`Recap delivered · ${sent} ${sent === 1 ? 'memory keeper' : 'memory keepers'}`);
        // Refresh status optimistically
        setStatus((prev) => ({
          ...(prev || {}),
          delivered: (prev?.delivered || 0) + sent,
          last_sent_at: new Date().toISOString(),
        }));
        closeModal();
      } else if (skipped > 0) {
        brandToast.whisper(`Already delivered to those keepers ✦ tick the resend to refresh`);
      } else {
        brandToast.slipped('No one to send to yet · guests need an email and a snap on the wall');
      }
    } catch (e) {
      brandToast.slipped(e.response?.data?.detail || 'The recap slipped away · please try again');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {delivered > 0 ? (
        <button
          type="button"
          onClick={openModal}
          data-testid="memory-recap-sent-pill"
          style={sentPill}
          title={lastSent ? `Last delivered ${new Date(lastSent).toLocaleString()}` : 'Recap delivered'}
        >
          <span aria-hidden style={{ fontSize: 12 }}>✦</span>
          Recap delivered · {delivered} {delivered === 1 ? 'keeper' : 'keepers'}
        </button>
      ) : (
        <button
          type="button"
          onClick={openModal}
          data-testid="memory-recap-send-pill"
          style={pillBase}
        >
          <span aria-hidden style={{ fontSize: 13 }}>✦</span>
          Send the recap
          {recipients > 0 && (
            <span style={{ opacity: 0.7, fontWeight: 400, letterSpacing: '0.04em', fontSize: 11 }}>
              · {recipients} {recipients === 1 ? 'keeper' : 'keepers'}
            </span>
          )}
        </button>
      )}

      {open && (
        <div style={overlayStyle} onClick={closeModal} data-testid="memory-recap-modal">
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div style={{
              padding: '18px 22px 14px',
              borderBottom: '1px solid rgba(212,175,94,0.18)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 12,
              background: 'linear-gradient(180deg, rgba(40,28,14,0.6) 0%, rgba(20,12,6,0.6) 100%)',
            }}>
              <div>
                <div style={{
                  fontFamily: 'Georgia, serif', fontStyle: 'italic',
                  fontSize: 11, letterSpacing: '0.20em', textTransform: 'uppercase',
                  color: '#A8854C', marginBottom: 4,
                }}>
                  A keepsake for those who came
                </div>
                <div style={{
                  fontFamily: 'Georgia, serif', fontSize: 18, color: '#FFE7A8',
                  letterSpacing: '-0.005em',
                }}>
                  Send the recap ✦
                </div>
                <div style={{
                  fontFamily: 'Georgia, serif', fontStyle: 'italic',
                  fontSize: 12, color: '#C9A87A', marginTop: 4, lineHeight: 1.5,
                }}>
                  Their snaps, a few moments from the wall, and your latest chapter — delivered like a private letter.
                </div>
              </div>
              <button
                type="button"
                onClick={closeModal}
                data-testid="memory-recap-close-btn"
                style={{
                  background: 'transparent', border: 'none', color: '#C9A87A',
                  fontSize: 22, cursor: 'pointer', padding: '4px 8px',
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div style={{ flex: 1, minHeight: 320, background: '#FAF6EE', overflow: 'hidden' }}>
              {loadingPreview ? (
                <div style={{
                  height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'Georgia, serif', fontStyle: 'italic', color: '#A8854C',
                }}>
                  composing the letter ✦
                </div>
              ) : (
                <iframe
                  data-testid="memory-recap-preview-iframe"
                  title="Recap preview"
                  srcDoc={previewHtml}
                  sandbox=""
                  style={{ width: '100%', height: '52vh', border: 0, background: '#FAF6EE' }}
                />
              )}
            </div>

            <div style={{
              padding: '14px 22px',
              borderTop: '1px solid rgba(212,175,94,0.18)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 16, flexWrap: 'wrap',
              background: '#140C06',
            }}>
              <div style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', color: '#C9A87A', fontSize: 13 }}>
                {recipients > 0 ? (
                  <>
                    Ready to send to <strong style={{ color: '#FFE7A8' }}>{recipients}</strong>{' '}
                    {recipients === 1 ? 'keeper' : 'keepers'}
                  </>
                ) : (
                  <>No keepers found yet — guests need an email and a snap on the wall.</>
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                {delivered > 0 && (
                  <label style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    fontFamily: 'Georgia, serif', fontStyle: 'italic', color: '#C9A87A',
                    fontSize: 12, cursor: 'pointer',
                  }}>
                    <input
                      type="checkbox"
                      checked={force}
                      onChange={(e) => setForce(e.target.checked)}
                      data-testid="memory-recap-force-resend-toggle"
                    />
                    Resend to everyone (refresh the chapter)
                  </label>
                )}
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={sending || recipients === 0}
                  data-testid="memory-recap-confirm-send-btn"
                  style={{
                    ...pillBase,
                    opacity: (sending || recipients === 0) ? 0.5 : 1,
                    cursor: (sending || recipients === 0) ? 'not-allowed' : 'pointer',
                  }}
                >
                  <span aria-hidden style={{ fontSize: 13 }}>✦</span>
                  {sending ? 'Delivering the letters…' : 'Send the recap'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
